import { useAuth } from "@elit-hair/elit-tools";
import { Button } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGuidedTourContext } from "@/components/guided-tour/guided-tour-context";
import { useSlackOAuthCallback } from "@/components/integrations/use-slack-oauth-callback";
import { ENV } from "@/constants/env";
import { cn } from "@/lib/utils";
import { IntegrationsCard } from "@/pages/settings/components/integrations-card";
import { SentryTest } from "@/pages/settings/components/sentry-test";
import { useCall } from "@/providers/hooks/use-call";
import { APP_ROUTES_FULL } from "@/utils/routes";

import { AudioDeviceManagement } from "./components/audio-device-management/audio-device-management";
import { SettingsPanel } from "./components/settings-panel";
import { Route as SettingsRoute } from "./route";

export const Route = createFileRoute(APP_ROUTES_FULL.SETTINGS)({
    component: Settings,
});

function Settings() {
    const { t } = useTranslation();
    const { userData, handleLogout } = useAuth();
    const { slack: slackStatus } = SettingsRoute.useSearch();

    const { isCallActive } = useCall();
    const [userPermissionsOpen, setUserPermissionsOpen] = useState(false);
    const [slackDialogOpen, setSlackDialogOpen] = useState(false);
    const { restartTour } = useGuidedTourContext();

    useSlackOAuthCallback({
        slackStatus,
        onStatus: () => setSlackDialogOpen(true),
    });

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-auto">
            <div className="flex flex-col bg-background shadow-sm p-4 gap-4 rounded-lg border border-border">
                <SettingsPanel />
            </div>

            <div className="flex flex-col bg-background shadow-sm p-4 gap-4 rounded-lg border border-border">
                <h3 className="text-lg font-semibold">{t("audio_settings")}</h3>
                <AudioDeviceManagement />
            </div>

            <div className="flex flex-col bg-background shadow-sm p-4 gap-4 rounded-lg border border-border">
                <IntegrationsCard slackDialogOpen={slackDialogOpen} onSlackDialogOpenChange={setSlackDialogOpen} />
            </div>

            <div className="flex flex-col bg-background shadow-sm p-4 gap-4 rounded-lg border border-border">
                <div className="flex flex-col gap-0 mb-2">
                    <h3 className="text-lg font-semibold">User: {userData?.name}</h3>
                    <h5 className="text-xs text-muted-foreground">ID: {userData?.id}</h5>
                </div>
                <div className="flex flex-col gap-1">
                    <Button variant="destructive" onClick={handleLogout} disabled={isCallActive()}>
                        {isCallActive() ? t("cannot_logout_during_call") : t("logout")}
                    </Button>
                </div>
                <Button variant="outline" onClick={() => setUserPermissionsOpen(!userPermissionsOpen)}>
                    {t("user_permissions")}
                </Button>

                {userPermissionsOpen && (
                    <div className="flex flex-col mt-2">
                        <div className="flex flex-wrap gap-1">
                            {userData?.permissionCodes.map((permission) => (
                                <div
                                    key={permission}
                                    className={cn(
                                        "px-2 py-1 border border-primary/20 bg-primary/10 rounded-full text-xs",
                                        "hover:text-sm hover:px-3 transition-all flex justify-center items-center"
                                    )}
                                >
                                    {permission}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <Button onClick={restartTour} variant="secondary">
                    {t("restart_tour")}
                </Button>
            </div>

            {ENV.DEV && (
                <div className="flex flex-col bg-background shadow-sm p-4 gap-4 rounded-lg border border-border md:col-span-2">
                    <SentryTest />
                </div>
            )}
        </div>
    );
}
